/**
 * Feedback-request banner shown when a cache convergence run parked itself
 * waiting on a human answer (`optimization.status === 'waiting_for_feedback'`,
 * spec `specs/pending/auto-maintain.md` Decisions 11-12). Renders in place of
 * `OptimizingBanner` — the two are mutually exclusive states of the same
 * `optimization` field, never shown together.
 *
 * The maintenance agent calls `request_feedback({ question, observed,
 * ifIntentional, ifBug })` when it can't tell whether an observed app change
 * is intentional or a bug, then parks the run. Any org member with
 * workflow-edit permission answers here (or dismisses to cancel the parked
 * run) — same policy as editing the flow.
 *
 * @module ui/pages/FlowDesigner/MaintenanceFeedbackBanner
 */
import React from 'react';
import { Radio, RadioGroup } from 'react-aria-components';
import { Question } from '@phosphor-icons/react';
import {
  Banner,
  cn,
  Text,
  Flex,
  Button,
  TextArea,
  Markdown,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
} from '@chatsdet/ui';
import type { MaintenanceFeedbackRequest } from './hooks/useMaintenanceFeedback';
import {
  useAnswerMaintenanceFeedback,
  useDismissMaintenanceFeedback,
} from './hooks/useMaintenanceFeedback';

const MARKDOWN_CLASSNAME =
  'prose prose-xs max-w-none text-foreground prose-p:text-foreground prose-li:text-foreground/90';

type MaintenanceFeedbackBannerProps = {
  workflowId: string;
  request: MaintenanceFeedbackRequest;
};

export function MaintenanceFeedbackBanner({ workflowId, request }: MaintenanceFeedbackBannerProps) {
  const [choice, setChoice] = React.useState<'intentional' | 'bug' | null>(null);
  const [explanation, setExplanation] = React.useState('');
  const [isDismissOpen, setIsDismissOpen] = React.useState(false);

  const answerMutation = useAnswerMaintenanceFeedback(workflowId);
  const dismissMutation = useDismissMaintenanceFeedback(workflowId);

  const OPTIONS = [
    { value: 'intentional', label: 'Yes, intentional', description: request.ifIntentional },
    { value: 'bug', label: 'No, it\u2019s a bug', description: request.ifBug },
  ] as const;

  const canSubmit = choice !== null && explanation.trim().length > 0 && !answerMutation.isPending;

  const handleSubmit = () => {
    if (!choice || explanation.trim().length === 0) return;
    answerMutation.mutate({ requestId: request.id, answer: choice, explanation: explanation.trim() });
  };

  const handleDismiss = () => {
    dismissMutation.mutate(request.id);
    setIsDismissOpen(false);
  };

  return (
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 w-full max-w-xl px-4">
      <Banner variant="warning" className="flex-col items-stretch gap-3 shadow-lg">
        <Flex align="center" gap="sm">
          <Question size={18} weight="fill" className="text-amber-600 dark:text-amber-400 shrink-0" />
          <Text size="sm" weight="medium">
            The maintenance agent needs your input
          </Text>
        </Flex>

        <Text size="sm" className="text-pretty">
          {request.question}
        </Text>
        <Markdown className={MARKDOWN_CLASSNAME}>{request.observedMd}</Markdown>

        <RadioGroup
          aria-label="Is this change intentional?"
          value={choice ?? ''}
          onChange={(next) => setChoice(next as 'intentional' | 'bug')}
          className="flex flex-col gap-2"
        >
          {OPTIONS.map(({ value, label, description }) => (
            <Radio
              key={value}
              value={value}
              className={({ isSelected, isFocusVisible }) =>
                cn(
                  'flex cursor-pointer flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
                  'hover:bg-muted/40',
                  isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border bg-card',
                  isFocusVisible && 'outline-none ring-2 ring-primary ring-offset-1'
                )
              }
            >
              <span className="text-sm font-medium text-foreground">{label}</span>
              <span className="text-xs text-pretty text-muted-foreground">{description}</span>
            </Radio>
          ))}
        </RadioGroup>

        {choice && (
          <TextArea
            label="Explanation"
            maxLength={4000}
            placeholder="Why?"
            value={explanation}
            onChange={setExplanation}
            rows={2}
            autoFocus
          />
        )}

        <Flex justify="between" align="center">
          <Button variant="ghost" onPress={() => setIsDismissOpen(true)}>
            Dismiss
          </Button>
          <Button variant="primary" onPress={handleSubmit} isDisabled={!canSubmit}>
            Submit
          </Button>
        </Flex>
      </Banner>

      <DialogTrigger isOpen={isDismissOpen} onOpenChange={setIsDismissOpen}>
        <span />
        <Dialog title="Dismiss maintenance run?">
          <DialogContent>
            <Text size="sm" className="text-muted-foreground">
              This cancels the parked maintenance run. The flow keeps running as-is; you can
              trigger maintenance again later.
            </Text>
          </DialogContent>
          <DialogFooter>
            <Button variant="outline" onPress={() => setIsDismissOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onPress={handleDismiss}>
              Dismiss
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </div>
  );
}
